import { useTranslation } from "react-i18next";

import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Label from "../../../../shared/ui/form/Label";
import { useCenterCourses, academicYearOptions } from "../../../center";

interface Props {
    centerId: string;
    courseId: string;
    year: string;
    onCourseChange: (courseId: string) => void;
    onYearChange: (year: string) => void;
    disabled?: boolean;
}

/** The academic years to offer, keeping one already chosen however old it is. */
export function useAcademicYearOptions(keep?: string) {
    return academicYearOptions(new Date(), keep ? Number(keep) : null);
}

/**
 * A training centre's student: which course they are in, and in which academic year.
 *
 * The same searchable pickers the lists filter with, so enrolling somebody and
 * finding them again read the same way. The academic year goes with the
 * course: with no course there is none to choose.
 */
export default function StudentCourseFields({
    centerId,
    courseId,
    year,
    onCourseChange,
    onYearChange,
    disabled = false,
}: Props) {
    const { t } = useTranslation();
    const { courses, loading } = useCenterCourses(centerId);
    const yearOptions = useAcademicYearOptions(year);

    const courseOptions = courses.map((course) => ({
        value: course.id,
        label: course.internal_name,
        subLabel: t(`center.courses.statuses.${course.status}`),
    }));

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
                <Label>{t("patient.course.course")}</Label>
                <FilterSelect
                    options={courseOptions}
                    value={courseId}
                    onChange={(value) => {
                        const next = (value as string) || "";
                        onCourseChange(next);
                        if (!next) onYearChange("");
                    }}
                    placeholder={loading ? t("common.loading") : t("patient.course.noCourse")}
                    searchPlaceholder={t("patient.searchPlaceholder")}
                    disabled={disabled}
                />
            </div>
            <div>
                <Label>{t("patient.course.year")}</Label>
                <FilterSelect
                    options={yearOptions}
                    value={year}
                    onChange={(value) => onYearChange((value as string) || "")}
                    placeholder={t("patient.course.noYear")}
                    searchPlaceholder={t("patient.searchPlaceholder")}
                    disabled={disabled || !courseId}
                />
            </div>
        </div>
    );
}
